import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usersAPI } from '@/services/api/users';
import type { Permission, RoleInfo } from '@/types/api';
import { buildRoutePermissions, buildMenuPermissions } from '@/config/routes';

interface PermissionContextType {
  userPermissions: Permission[];
  rolePermissionsMap: Record<string, RoleInfo>;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  canAccessRoute: (route: string) => boolean;
  canShowMenu: (menuKey: string) => boolean;
  loading: boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// 从统一配置构建权限映射
const ROUTE_PERMISSIONS = buildRoutePermissions();
const MENU_PERMISSIONS = buildMenuPermissions();

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userInfo, loading: authLoading } = useAuth();
  const [rolePermissionsMap, setRolePermissionsMap] = useState<Record<string, RoleInfo>>({});
  const [roleLoading, setRoleLoading] = useState(true);

  // 总体 loading 状态：需要等待用户信息和角色权限都加载完成
  const loading = authLoading || roleLoading;

  // 获取所有角色的权限信息（只在应用启动时获取一次）
  useEffect(() => {
    let hasCachedData = false;

    // 先尝试从缓存加载
    const cached = sessionStorage.getItem('rolePermissions');
    if (cached) {
      try {
        setRolePermissionsMap(JSON.parse(cached));
        setRoleLoading(false);
        hasCachedData = true;
      } catch (e) {
        console.error('解析缓存权限数据失败:', e);
      }
    }

    const fetchRolePermissions = async () => {
      try {
        // 如果没有缓存数据才显示 loading
        if (!hasCachedData) {
          setRoleLoading(true);
        }
        const data = await usersAPI.getRolePermissions();
        setRolePermissionsMap(data);

        // 缓存到 sessionStorage，页面刷新时可以快速恢复
        sessionStorage.setItem('rolePermissions', JSON.stringify(data));
      } catch (error) {
        console.error('获取角色权限失败:', error);

        // 如果请求失败且没有缓存，尝试再次从缓存中恢复
        if (!hasCachedData) {
          const cachedFallback = sessionStorage.getItem('rolePermissions');
          if (cachedFallback) {
            try {
              setRolePermissionsMap(JSON.parse(cachedFallback));
            } catch (e) {
              console.error('解析缓存权限数据失败:', e);
            }
          }
        }
      } finally {
        setRoleLoading(false);
      }
    };

    fetchRolePermissions();
  }, []);

  // 获取当前用户的权限列表
  const userPermissions = useMemo(() => {
    if (!userInfo?.role || !Object.keys(rolePermissionsMap).length) {
      return [];
    }
    
    const roleInfo = rolePermissionsMap[userInfo.role];
    return roleInfo?.permissions || [];
  }, [userInfo?.role, rolePermissionsMap]);

  // 检查用户是否有指定权限
  const hasPermission = (permission: Permission): boolean => {
    if (!userInfo) return false;
    return userPermissions.includes(permission);
  };

  // 检查用户是否有任一权限
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!userInfo) return false;
    return permissions.some(permission => userPermissions.includes(permission));
  };

  // 检查用户是否有所有权限
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    if (!userInfo) return false;
    return permissions.every(permission => userPermissions.includes(permission));
  };

  // 检查路由是否可以访问
  const canAccessRoute = (route: string): boolean => {
    const permission = ROUTE_PERMISSIONS[route];
    if (!permission) return true;
    return hasPermission(permission);
  };

  // 检查菜单项是否可以显示
  const canShowMenu = (menuKey: string): boolean => {
    const permission = MENU_PERMISSIONS[menuKey];
    if (!permission) return true;
    return hasPermission(permission);
  };

  const value: PermissionContextType = {
    userPermissions,
    rolePermissionsMap,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessRoute,
    canShowMenu,
    loading,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};
